import { isArray, isEmpty } from 'radash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Loader } from '@/components/core/Loader';
import { Select, SelectContent, SelectTrigger } from '@/components/core/Select';

import { FloatComponent, HTMLComponent, IntegerComponent, StringComponent, TextareaComponent } from '..';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useOptions } from '../hooks/useOptions';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { ClearButton } from '../Layout/ClearButton';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';
import { DimensionSelectItem } from './DimensionSelectItem';

interface DimensionalComponentProps extends EditorComponent {
    className?: string;
}

const DimensionalComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    readonly = false,
    hidden = false,
    gridMode = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    className,
    labelClassName,
}: DimensionalComponentProps) => {

    const focusRef = useFocusOnAttribute<HTMLButtonElement>(attribute);

    const { options: dimensions, loading: loadingDimensions } = useOptions({ attribute, item: attribute.item });
    const [selectedDimension, setSelectedDimension] = useState<string | undefined>();

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const previousDimensionKeysRef = useRef<string | null>(null);

    useEffect(() => {
        if (loadingDimensions || !dimensions?.length) return;

        const dimensionKeys = dimensions.map(d => d.key).join(',');
        const listChanged = previousDimensionKeysRef.current !== dimensionKeys;

        if (listChanged) {
            previousDimensionKeysRef.current = dimensionKeys;
            setSelectedDimension(dimensions[0]?.key);
            return;
        }

        if (selectedDimension && dimensions.some(d => d.key === selectedDimension)) return;

        setSelectedDimension(dimensions[0]?.key);
    }, [dimensions, loadingDimensions, selectedDimension]);

    const currentFieldPath = selectedDimension ? `${fieldPath}.${selectedDimension}` : fieldPath;
    const { validationErrors: allValidationErrors, validationWarnings: allValidationWarnings } = useValidationsStore(useShallow(state => ({
        validationErrors: state.validationErrors || {},
        validationWarnings: state.validationWarnings || {},
    })));

    const validationErrors = allValidationErrors[fieldPath] as Validation[] | undefined;
    const validationWarnings = allValidationWarnings[fieldPath] as Validation[] | undefined;
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);

    /**
     * Returns the type of the attribute that the value of the multi dimensional should have.
     * First it checks the `renderer` params and then falls back to the `valueClass` property.
     */
    const getValueAttributeType = () => {

        const renderer = attribute.params?.renderer || attribute.params?.customRenderer;
        const rendererParams: { [key:string]: string | number } = {};

        if (isArray(renderer)) {
            renderer.forEach(renderingParam => rendererParams[renderingParam.key] = renderingParam.value);
        }

        const rendererType = rendererParams['type'];
        if (rendererType) {
            return rendererType;
        } else {
            // Return `valueClass`
            return attribute.params?.valueClass;
        }

    };

    const renderValueAttribute = useCallback((props: EditorComponent & { key: string }) => {
        const type = getValueAttributeType();

        const fieldValidationErrors = allValidationErrors?.[fieldPath] as Validation[] | undefined;
        const fieldValidationWarnings = allValidationWarnings?.[fieldPath] as Validation[] | undefined;
        const fieldHasErrors = !isEmpty(fieldValidationErrors);
        const fieldHasWarnings = !isEmpty(fieldValidationWarnings);

        switch(type) {
            case 'Float':
                return (
                    <FloatComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            case 'Number':
            case 'Integer':
                return (
                    <IntegerComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            case 'MultiLineDimensional':
                return (
                    <TextareaComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            case 'HTMLDimensional':
                return (
                    <HTMLComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />
                );
            default: // and 'String'
                return (
                    <StringComponent
                        {...props}
                        hasErrors={fieldHasErrors}
                        hasWarnings={fieldHasWarnings}
                        isDimensional={true}
                        gridMode={gridMode}
                        className={className}
                    />);
        }
    }, [allValidationErrors, allValidationWarnings, fieldPath, gridMode, className]);

    if (hidden) {
        return null;
    }

    if (loadingDimensions || selectedDimension == undefined) {
        return (
            <Layout attribute={attribute} hideLabel={false} direction={direction} fieldPath={fieldPath} gridMode={gridMode}
                isClearable={true} labelClassName={labelClassName}>
                <Loader />
            </Layout>
        );
    }

    /**
     * TODO:
     * Dimensional attributes can use different types of attributes which is governed by `attribute.params?.valueClass`.
     *
     * For example: Float
     */
    return (
        <Layout
            attribute={attribute}
            hideLabel={gridMode}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
            labelVerticalAlign="top"
        >
            <div className="tw-flex tw-flex-row tw-w-full">
                <div className="tw-w-[80px]">
                    <Select value={selectedDimension} onValueChange={setSelectedDimension}>
                        <SelectTrigger borderless={true} ref={focusRef} className={`tw-w-full lg-font-dynamic tw-text-t-primary tw-h-[36px] ${hasErrors ?
                            'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-gray-100`}>
                            <span className="tw-text-bold tw-text-black">
                                {selectedDimension}
                            </span>
                        </SelectTrigger>
                        <SelectContent className="tw-w-[500px] tw-max-h-[400px] tw-overflow-y-auto">
                            {dimensions.map(dimension =>
                                <DimensionSelectItem key={dimension.key} dimension={dimension} fieldPath={`${fieldPath}[${dimension.key}]`} attribute={attribute} />)}
                        </SelectContent>
                    </Select>
                </div>
                <div className={`tw-w-full ${gridMode? 'tw-pl-2':''}`}>
                    {
                        renderValueAttribute({
                            key: currentFieldPath,
                            fieldPath: currentFieldPath,
                            hideLabel: true,
                            attribute,
                            readonly,
                        })
                    }
                </div>
                {!readonly && <div className='tw-absolute tw-top-0 tw-right-0 tw-mr-2.5 tw-mt-0'>
                    <ClearButton
                        attributeName={attribute.name}
                        fieldPath={currentFieldPath}
                    />
                </div>}
            </div>
        </Layout>
    );

};

export { DimensionalComponent };
